import * as React from "react";
import { useConversationRecording } from "../hooks/useConversationRecording";
import { useCompleteConversationLog } from "../api/mutations/useConversationLogMutations";
import { AllChartsData } from "../hooks/useAllChartsData";
import { useAllChartsDataContext } from "./AllChartsDataContext";

interface HHERAllyContextValue {
  isAvailable: boolean;
  isPanelOpen: boolean;
  isRecording: boolean;
  isUploading: boolean;
  activeConversationLogId: string | null;
  transcript: string;
  focusedSectionCode: string | undefined;
  /**
   * Whether the panel's active Summary tab request (useMdmAiSuggestions /
   * useSectionCiSuggestions) is currently in flight. Bridges the panel's real
   * request state up to the global HHERAllyToggle trigger so it can mirror
   * Suggested Orders' typewriter/loading treatment without a fixed timeout.
   */
  isSummaryLoading: boolean;
  openPanel: (sectionCode?: string) => void;
  closePanel: () => void;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  setTranscript: (text: string) => void;
  setSummaryLoading: (isLoading: boolean) => void;
}

interface HHERAllyProviderProps {
  encounterId: string;
  chartCode: string;
  isAvailable?: boolean;
  children: React.ReactNode;
}

const HHERAllyContext = React.createContext<HHERAllyContextValue>({
  isAvailable: false,
  isPanelOpen: false,
  isRecording: false,
  isUploading: false,
  activeConversationLogId: null,
  transcript: "",
  focusedSectionCode: undefined,
  isSummaryLoading: false,
  openPanel: () => undefined,
  closePanel: () => undefined,
  startListening: async () => undefined,
  stopListening: async () => undefined,
  setTranscript: () => undefined,
  setSummaryLoading: () => undefined,
});

function useHHERAllyContext(): HHERAllyContextValue {
  return React.useContext(HHERAllyContext);
}

const MAX_CHART_STRUCTURE_SECTIONS = 50;
const MAX_FIELDS_PER_SECTION = 200;
const MAX_AVAILABLE_ORDERS = 500;

function buildChartStructure(
  allChartsData?: AllChartsData
): Record<string, string[]> | null {
  if (!allChartsData?.charts?.length) {
    return null;
  }

  const structure: Record<string, string[]> = {};
  for (const chart of allChartsData.charts) {
    for (const section of chart.sections ?? []) {
      const fields = (section.subsections ?? []).flatMap((subsection) =>
        (subsection.fields ?? []).map((field) => field.label)
      );
      structure[section.sectionName] = [
        ...(structure[section.sectionName] ?? []),
        ...fields,
      ];
    }
  }

  const trimmed: Record<string, string[]> = {};
  Object.entries(structure)
    .slice(0, MAX_CHART_STRUCTURE_SECTIONS)
    .forEach(([sectionName, fields]) => {
      trimmed[sectionName] = fields.slice(0, MAX_FIELDS_PER_SECTION);
    });
  return trimmed;
}

const HHERAllyProvider: React.FC<HHERAllyProviderProps> = ({
  encounterId,
  chartCode,
  isAvailable = true,
  children,
}) => {
  const [isPanelOpen, setIsPanelOpen] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<string>("");
  const [focusedSectionCode, setFocusedSectionCode] = React.useState<
    string | undefined
  >(undefined);
  const [isSummaryLoading, setSummaryLoading] = React.useState<boolean>(false);

  const {
    isRecording,
    isUploading,
    startListening: startRecording,
    stopListening: stopRecording,
    cancelListening: cancelRecording,
    conversationLogId,
  } = useConversationRecording({ encounterId, chartCode });
  const completeMutation = useCompleteConversationLog();
  const { allChartsData, allOrderNames } = useAllChartsDataContext();

  React.useEffect(() => {
    if (isAvailable) {
      return;
    }

    setIsPanelOpen(false);
    setTranscript("");
    setFocusedSectionCode(undefined);
    setSummaryLoading(false);

    if (isRecording) {
      cancelRecording();
    }
  }, [isAvailable, isRecording, cancelRecording]);

  const openPanel = React.useCallback((sectionCode?: string) => {
    setFocusedSectionCode(sectionCode);
    setIsPanelOpen(true);
  }, []);

  const closePanel = React.useCallback(() => {
    setFocusedSectionCode(undefined);
    setIsPanelOpen(false);
    setSummaryLoading(false);
  }, []);

  const handleSetTranscript = React.useCallback((text: string) => {
    setTranscript(text);
  }, []);

  const startListening = React.useCallback(async () => {
    await startRecording();
  }, [startRecording]);

  const stopListening = React.useCallback(async () => {
    const { audioDuration } = await stopRecording();

    if (!conversationLogId) {
      return;
    }

    await completeMutation.mutateAsync({
      conversationLogId,
      encounterId,
      request: {
        transcriptText: "",
        audioDurationSeconds: Math.max(1, audioDuration),
        chartStructure: buildChartStructure(allChartsData),
        availableOrders: (allOrderNames ?? []).slice(0, MAX_AVAILABLE_ORDERS),
      },
    });
  }, [
    stopRecording,
    completeMutation,
    conversationLogId,
    encounterId,
    allChartsData,
    allOrderNames,
  ]);

  const value: HHERAllyContextValue = {
    isAvailable,
    isPanelOpen,
    isRecording,
    isUploading,
    activeConversationLogId: conversationLogId,
    transcript,
    focusedSectionCode,
    isSummaryLoading,
    openPanel,
    closePanel,
    startListening,
    stopListening,
    setTranscript: handleSetTranscript,
    setSummaryLoading,
  };

  return (
    <HHERAllyContext.Provider value={value}>
      {children}
    </HHERAllyContext.Provider>
  );
};

const HHERAllyContextProvider = HHERAllyProvider;

export { HHERAllyProvider, HHERAllyContextProvider, useHHERAllyContext };
export type { HHERAllyContextValue, HHERAllyProviderProps };
